import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, Stamp, Gift, Activity, Plus, Minus, Search, UserPlus, Trash2, CheckCircle2,
  CalendarDays, Clock, Phone, Check, X, MessageCircle, ScanLine, QrCode, Download, Share2, LogOut,
  Camera, ImagePlus, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { api, STAMPS_PER_REWARD, getToken, clearToken, photoSrc } from "@/lib/api";
import { PunchCard } from "@/components/PunchCard";
import { QrScannerDialog } from "@/components/QrScannerDialog";
import { GestoreLogin } from "@/components/GestoreLogin";
import { waLinkTo, BAR_NAME } from "@/lib/config";

const StatCard = ({ icon: Icon, label, value, testid, highlight, onClick }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`text-left w-full rounded-xl p-5 ring-1 transition-all duration-300 hover:-translate-y-1 hover:ring-primary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
      highlight ? "bg-primary/10 ring-primary/40" : "bg-zinc-900 ring-zinc-800"
    }`}>
    <Icon className={`w-5 h-5 mb-3 ${highlight ? "text-primary" : "text-primary"}`} />
    <p className="font-sans font-bold text-4xl tracking-tight">{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{label}</p>
  </button>
);

export default function OwnerPage() {
  const [authed, setAuthed] = useState(!!getToken());
  const [tab, setTab] = useState("clienti");
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [appQrOpen, setAppQrOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [ownerFile, setOwnerFile] = useState(null);
  const [ownerCaption, setOwnerCaption] = useState("");
  const ownerFileRef = useRef(null);
  const appQrRef = useRef(null);
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";
  const installUrl = `${appUrl}/installa`;

  const logout = () => { clearToken(); setAuthed(false); };

  const refresh = useCallback(async () => {
    try {
      const [s, list, res, ph] = await Promise.all([api.stats(), api.listCustomers(), api.listReservations(), api.listPhotos()]);
      setStats(s);
      setCustomers(list);
      setReservations(res);
      setPhotos(ph);
      setSelected((prev) => (prev ? list.find((c) => c.id === prev.id) || null : null));
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); setAuthed(false); }
    }
  }, []);

  useEffect(() => { if (authed) refresh(); }, [authed, refresh]);

  const filtered = customers.filter(
    (c) => c.name.toLowerCase().includes(query.toLowerCase()) || c.phone.includes(query)
  );

  const quickStamp = async (c, e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      const before = c.stamps;
      const updated = await api.addStamp(c.id);
      if (before === STAMPS_PER_REWARD - 1) toast.success(`🎁 ${c.name} ha sbloccato un premio!`);
      else toast.success(`+1 timbro · ${c.name} (${updated.stamps}/${STAMPS_PER_REWARD})`);
      await refresh();
      setSelected(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const handleScan = async (text) => {
    const id = text?.startsWith("MIRO:") ? text.slice(5) : text;
    if (!id) return toast.error("QR non valido");
    setBusy(true);
    try {
      const before = customers.find((c) => c.id === id);
      const updated = await api.addStamp(id);
      if (before && before.stamps === STAMPS_PER_REWARD - 1)
        toast.success(`🎁 ${updated.name} ha sbloccato un premio!`);
      else toast.success(`✅ +1 timbro · ${updated.name} (${updated.stamps}/${STAMPS_PER_REWARD})`);
      await refresh();
      setSelected(updated);
    } catch (err) {
      toast.error("QR non valido o cliente non trovato");
    } finally { setBusy(false); }
  };

  const downloadAppQr = () => {
    const canvas = appQrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = "miro-app-qr.png";
    a.click();
  };

  const doAction = async (fn, msg) => {
    setBusy(true);
    try {
      const updated = await fn();
      if (msg) toast.success(msg);
      await refresh();
      if (updated && updated.id) setSelected(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const createCustomer = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newPhone.trim()) return;
    setBusy(true);
    try {
      const c = await api.createCustomer(newName.trim(), newPhone.trim());
      toast.success("Cliente aggiunto");
      setNewName(""); setNewPhone(""); setAddOpen(false);
      await refresh(); setSelected(c);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const resAction = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const photoAction = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const ownerUpload = async (e) => {
    e.preventDefault();
    if (!ownerFile) { toast.error("Scegli un'immagine"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", ownerFile);
      fd.append("caption", ownerCaption.trim());
      await api.ownerUploadPhoto(fd);
      toast.success("Foto pubblicata");
      setOwnerFile(null); setOwnerCaption(""); setPhotoUploadOpen(false);
      if (ownerFileRef.current) ownerFileRef.current.value = "";
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const statusBadge = (s) => {
    const map = {
      pending: "bg-amber-500/15 text-amber-400 ring-amber-500/30",
      confirmed: "bg-emerald-500/15 text-emerald-400 ring-emerald-500/30",
      cancelled: "bg-red-500/15 text-red-400 ring-red-500/30",
    };
    const label = { pending: "In attesa", confirmed: "Confermata", cancelled: "Annullata" };
    return <span className={`text-xs px-2.5 py-1 rounded-full ring-1 ${map[s]}`}>{label[s]}</span>;
  };

  if (!authed) return <GestoreLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="relative z-[2] max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] text-primary mb-2">PANNELLO GESTORE</p>
          <h1 className="font-serif text-4xl sm:text-5xl">Gestione MI.RO'</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="open-scanner" onClick={() => setScanOpen(true)} className="rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold h-11 px-6 transition-colors duration-300">
            <ScanLine className="w-4 h-4 mr-2" /> Scansiona QR
          </Button>
          <Button data-testid="open-app-qr" onClick={() => setAppQrOpen(true)} variant="outline" className="rounded-full h-11 px-5 border-zinc-700 bg-transparent hover:bg-zinc-800">
            <QrCode className="w-4 h-4 mr-2" /> QR dell'app
          </Button>
          <Button data-testid="logout-btn" onClick={logout} variant="outline" className="rounded-full h-11 px-5 border-zinc-700 bg-transparent hover:bg-zinc-800 text-muted-foreground">
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-add-customer" variant="outline" className="rounded-full h-11 px-5 border-zinc-700 bg-transparent hover:bg-zinc-800">
                <UserPlus className="w-4 h-4 mr-2" /> Nuovo cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800">
              <DialogHeader><DialogTitle className="font-serif text-2xl">Aggiungi cliente</DialogTitle></DialogHeader>
              <form onSubmit={createCustomer} className="space-y-4 pt-2">
                <Input data-testid="new-customer-name" placeholder="Nome cliente" value={newName} onChange={(e) => setNewName(e.target.value)} className="h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-primary" />
                <Input data-testid="new-customer-phone" type="tel" placeholder="Numero di telefono" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} className="h-11 bg-zinc-900 border-zinc-800 focus-visible:ring-primary" />
                <DialogFooter>
                  <Button data-testid="submit-new-customer" type="submit" disabled={busy} className="rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold">Crea tessera</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <QrScannerDialog open={scanOpen} onOpenChange={setScanOpen} onScan={handleScan} />

      <Dialog open={appQrOpen} onOpenChange={setAppQrOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="app-qr-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Scarica l'app {BAR_NAME}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 pt-2">
            <div ref={appQrRef} className="bg-white p-4 rounded-2xl">
              <QRCodeCanvas value={installUrl} size={220} level="M" fgColor="#0a0a0a" bgColor="#ffffff" marginSize={2} data-testid="app-qr-canvas" />
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              Il cliente inquadra questo QR (anche da Instagram) per aprire la pagina di installazione, salvare la tessera e installare l'app sul telefono.
            </p>
            <div className="flex gap-3 w-full">
              <Button data-testid="download-app-qr" onClick={downloadAppQr} className="flex-1 rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold h-11">
                <Download className="w-4 h-4 mr-2" /> Scarica PNG
              </Button>
              {typeof navigator !== "undefined" && navigator.share && (
                <Button data-testid="share-app-link" onClick={() => navigator.share({ title: BAR_NAME, url: installUrl }).catch(() => {})} variant="outline" className="flex-1 rounded-full h-11 border-zinc-700 bg-transparent hover:bg-zinc-800">
                  <Share2 className="w-4 h-4 mr-2" /> Condividi
                </Button>
              )}
            </div>
            <p className="font-mono text-xs text-muted-foreground break-all text-center">{installUrl}</p>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 mb-10">
        <StatCard testid="stat-customers" icon={Users} label="Clienti" value={stats?.total_customers ?? "—"} onClick={() => setTab("clienti")} />
        <StatCard testid="stat-stamps" icon={Stamp} label="Timbri erogati" value={stats?.total_stamps ?? "—"} onClick={() => setTab("clienti")} />
        <StatCard testid="stat-redeemed" icon={Gift} label="Premi riscattati" value={stats?.rewards_redeemed ?? "—"} onClick={() => setTab("clienti")} />
        <StatCard testid="stat-active" icon={Activity} label="Tessere attive" value={stats?.active_cards ?? "—"} onClick={() => setTab("clienti")} />
        <StatCard testid="stat-pending-res" icon={CalendarDays} label="Prenotazioni da confermare" value={stats?.pending_reservations ?? "—"} highlight={stats?.pending_reservations > 0} onClick={() => setTab("prenotazioni")} />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-secondary/60 rounded-full p-1 mb-8">
          <TabsTrigger value="clienti" data-testid="tab-clienti" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">Fedeltà</TabsTrigger>
          <TabsTrigger value="prenotazioni" data-testid="tab-prenotazioni" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">
            Prenotazioni {stats?.pending_reservations > 0 && <span className="ml-2 text-xs bg-primary text-primary-foreground rounded-full px-1.5">{stats.pending_reservations}</span>}
          </TabsTrigger>
          <TabsTrigger value="foto" data-testid="tab-foto" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">
            Foto {photos.filter((p) => p.status === "pending").length > 0 && <span className="ml-2 text-xs bg-primary text-primary-foreground rounded-full px-1.5">{photos.filter((p) => p.status === "pending").length}</span>}
          </TabsTrigger>
        </TabsList>

        {/* CLIENTI */}
        <TabsContent value="clienti">
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="search-customer" placeholder="Cerca per nome o telefono" value={query} onChange={(e) => setQuery(e.target.value)} className="h-11 pl-10 bg-zinc-900 border-zinc-800 focus-visible:ring-primary" />
              </div>
              <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1" data-testid="customer-list">
                {filtered.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">Nessun cliente trovato.</p>}
                {filtered.map((c) => (
                  <div key={c.id} data-testid={`customer-row-${c.id}`}
                    className={`flex items-center gap-2 rounded-xl p-3 ring-1 transition-colors duration-200 ${selected?.id === c.id ? "bg-primary/10 ring-primary/50" : "bg-zinc-900 ring-zinc-800 hover:ring-zinc-700"}`}>
                    <button onClick={() => setSelected(c)} className="flex-1 text-left min-w-0">
                      <p className="font-medium truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.phone}</p>
                    </button>
                    {c.rewards_available > 0 && <span className="flex items-center gap-1 text-xs text-primary shrink-0"><Gift className="w-3.5 h-3.5" />{c.rewards_available}</span>}
                    <span className="font-mono text-sm text-muted-foreground shrink-0">{c.stamps}/{STAMPS_PER_REWARD}</span>
                    <button data-testid={`quick-stamp-${c.id}`} onClick={(e) => quickStamp(c, e)} disabled={busy}
                      title="Aggiungi timbro" className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-200">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-3">
              {selected ? (
                <motion.div key={selected.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl bg-zinc-900 ring-1 ring-zinc-800 p-6 sm:p-8" data-testid="customer-detail">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="font-serif text-3xl" data-testid="detail-name">{selected.name}</h3>
                      <p className="text-sm text-muted-foreground">{selected.phone}</p>
                    </div>
                    <button onClick={() => doAction(async () => { await api.deleteCustomer(selected.id); setSelected(null); }, "Cliente eliminato")}
                      data-testid="delete-customer-btn" className="text-muted-foreground hover:text-destructive transition-colors p-2" title="Elimina">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <PunchCard stamps={selected.stamps} />
                  <div className="flex flex-wrap items-center gap-3 mt-8">
                    <Button data-testid="add-stamp-btn" onClick={(e) => quickStamp(selected, e)} disabled={busy} className="rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold h-11 px-6 transition-colors duration-300"><Plus className="w-4 h-4 mr-1" /> Aggiungi timbro</Button>
                    <Button data-testid="remove-stamp-btn" onClick={() => doAction(() => api.removeStamp(selected.id), "Timbro rimosso")} disabled={busy || selected.stamps === 0} variant="outline" className="rounded-full h-11 px-5 border-zinc-700 bg-transparent hover:bg-zinc-800"><Minus className="w-4 h-4 mr-1" /> Rimuovi</Button>
                    <Button data-testid="redeem-btn" onClick={() => doAction(() => api.redeem(selected.id), "Premio riscattato!")} disabled={busy || selected.rewards_available === 0} className="rounded-full h-11 px-6 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors duration-300 disabled:opacity-40"><CheckCircle2 className="w-4 h-4 mr-1" /> Riscatta premio</Button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-8">
                    <div className="rounded-xl bg-black/30 ring-1 ring-white/5 p-4"><p className="font-serif text-2xl text-primary" data-testid="detail-rewards-available">{selected.rewards_available}</p><p className="text-xs text-muted-foreground">Premi disponibili</p></div>
                    <div className="rounded-xl bg-black/30 ring-1 ring-white/5 p-4"><p className="font-serif text-2xl">{selected.rewards_redeemed}</p><p className="text-xs text-muted-foreground">Riscattati</p></div>
                    <div className="rounded-xl bg-black/30 ring-1 ring-white/5 p-4"><p className="font-serif text-2xl">{selected.total_stamps}</p><p className="text-xs text-muted-foreground">Acquisti totali</p></div>
                  </div>
                </motion.div>
              ) : (
                <div className="rounded-2xl bg-zinc-900/40 ring-1 ring-dashed ring-zinc-800 p-12 h-full flex flex-col items-center justify-center text-center">
                  <Stamp className="w-10 h-10 text-muted-foreground/40 mb-4" />
                  <p className="text-muted-foreground">Usa il pulsante <span className="text-primary">+</span> sulla lista per timbrare al volo, o seleziona un cliente per i dettagli.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* PRENOTAZIONI */}
        <TabsContent value="prenotazioni">
          <div className="space-y-3" data-testid="reservations-list">
            {reservations.length === 0 && (
              <div className="rounded-2xl bg-zinc-900/40 ring-1 ring-dashed ring-zinc-800 p-12 text-center">
                <CalendarDays className="w-10 h-10 text-muted-foreground/40 mb-4 mx-auto" />
                <p className="text-muted-foreground">Nessuna prenotazione al momento.</p>
              </div>
            )}
            {reservations.map((r) => (
              <div key={r.id} data-testid={`reservation-${r.id}`}
                className="rounded-xl bg-zinc-900 ring-1 ring-zinc-800 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="font-medium text-lg">{r.name}</p>
                    {statusBadge(r.status)}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-primary" /> {r.date}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-primary" /> {r.time}</span>
                    <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-primary" /> {r.guests} pers.</span>
                    <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-primary" /> {r.phone}</span>
                  </div>
                  {r.notes && <p className="text-sm text-muted-foreground/80 mt-2 italic">"{r.notes}"</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a href={waLinkTo(r.phone, `Ciao ${r.name}, confermiamo la tua prenotazione da MI.RO' per il ${r.date} alle ${r.time} (${r.guests} persone). A presto!`)}
                    target="_blank" rel="noopener noreferrer" data-testid={`res-whatsapp-${r.id}`}
                    className="flex items-center justify-center w-9 h-9 rounded-full bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366] hover:text-white transition-colors" title="Scrivi su WhatsApp">
                    <MessageCircle className="w-4 h-4" />
                  </a>
                  {r.status !== "confirmed" && (
                    <button data-testid={`res-confirm-${r.id}`} onClick={() => resAction(() => api.updateReservation(r.id, "confirmed"), "Prenotazione confermata")}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500 hover:text-white transition-colors" title="Conferma"><Check className="w-4 h-4" /></button>
                  )}
                  {r.status !== "cancelled" && (
                    <button data-testid={`res-cancel-${r.id}`} onClick={() => resAction(() => api.updateReservation(r.id, "cancelled"), "Prenotazione annullata")}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-red-500/15 text-red-400 hover:bg-red-500 hover:text-white transition-colors" title="Annulla"><X className="w-4 h-4" /></button>
                  )}
                  <button data-testid={`res-delete-${r.id}`} onClick={() => resAction(() => api.deleteReservation(r.id), "Prenotazione eliminata")}
                    className="flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-destructive transition-colors" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* FOTO */}
        <TabsContent value="foto">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-muted-foreground">
              Le foto dei clienti compaiono qui in attesa: <span className="text-white">approva</span> quelle da pubblicare. Le tue foto sono pubblicate subito.
            </p>
            <Dialog open={photoUploadOpen} onOpenChange={setPhotoUploadOpen}>
              <DialogTrigger asChild>
                <Button data-testid="owner-upload-photo" className="rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold h-11 px-6 shrink-0">
                  <ImagePlus className="w-4 h-4 mr-2" /> Carica foto
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-950 border-zinc-800">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Carica una foto ufficiale</DialogTitle></DialogHeader>
                <form onSubmit={ownerUpload} className="space-y-4 pt-2">
                  <input ref={ownerFileRef} type="file" accept="image/*" className="hidden" data-testid="owner-photo-file"
                    onChange={(e) => setOwnerFile(e.target.files?.[0] || null)} />
                  <button type="button" onClick={() => ownerFileRef.current?.click()}
                    className="w-full aspect-video rounded-xl ring-1 ring-dashed ring-white/20 bg-white/[0.02] hover:ring-primary/50 transition-colors flex items-center justify-center overflow-hidden">
                    {ownerFile ? (
                      <img src={URL.createObjectURL(ownerFile)} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center text-muted-foreground"><ImagePlus className="w-8 h-8 mb-2 text-primary" /><span className="text-sm">Scegli un'immagine</span></div>
                    )}
                  </button>
                  <Textarea data-testid="owner-photo-caption" placeholder="Didascalia (opzionale)" value={ownerCaption} onChange={(e) => setOwnerCaption(e.target.value)} className="bg-zinc-900 border-zinc-800 focus-visible:ring-primary" />
                  <DialogFooter>
                    <Button data-testid="owner-photo-submit" type="submit" disabled={busy} className="rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold">Pubblica</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {photos.length === 0 ? (
            <div className="rounded-2xl bg-zinc-900/40 ring-1 ring-dashed ring-zinc-800 p-12 text-center">
              <Camera className="w-10 h-10 text-muted-foreground/40 mb-4 mx-auto" />
              <p className="text-muted-foreground">Nessuna foto al momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photos-moderation-grid">
              {photos.map((p) => (
                <div key={p.id} data-testid={`photo-${p.id}`} className="rounded-xl overflow-hidden ring-1 ring-zinc-800 bg-zinc-900">
                  <div className="relative aspect-square">
                    <img src={photoSrc(p.url)} alt={p.caption || p.name} className="w-full h-full object-cover" />
                    <span className={`absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full ring-1 ${
                      p.status === "approved" ? "bg-emerald-500/20 text-emerald-300 ring-emerald-500/40"
                      : p.status === "pending" ? "bg-amber-500/20 text-amber-300 ring-amber-500/40"
                      : "bg-red-500/20 text-red-300 ring-red-500/40"}`}>
                      {p.status === "approved" ? "Pubblicata" : p.status === "pending" ? "In attesa" : "Rifiutata"}
                    </span>
                    {p.source === "owner" && <span className="absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full bg-primary/25 text-primary ring-1 ring-primary/40">MI.RO'</span>}
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-muted-foreground truncate">{p.name}{p.caption ? ` · ${p.caption}` : ""}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1" data-testid={`photo-views-${p.id}`}><Eye className="w-3 h-3 text-primary" /> {p.views || 0}</span>
                      <span className="flex items-center gap-1" data-testid={`photo-shares-${p.id}`}><Share2 className="w-3 h-3 text-primary" /> {p.shares || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      {p.status !== "approved" && (
                        <button data-testid={`photo-approve-${p.id}`} onClick={() => photoAction(() => api.moderatePhoto(p.id, "approved"), "Foto pubblicata")}
                          className="flex-1 flex items-center justify-center h-9 rounded-full bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500 hover:text-white transition-colors" title="Approva"><Check className="w-4 h-4" /></button>
                      )}
                      {p.status !== "rejected" && (
                        <button data-testid={`photo-reject-${p.id}`} onClick={() => photoAction(() => api.moderatePhoto(p.id, "rejected"), "Foto rifiutata")}
                          className="flex-1 flex items-center justify-center h-9 rounded-full bg-amber-500/15 text-amber-400 hover:bg-amber-500 hover:text-white transition-colors" title="Rifiuta"><X className="w-4 h-4" /></button>
                      )}
                      <button data-testid={`photo-delete-${p.id}`} onClick={() => photoAction(() => api.deletePhoto(p.id), "Foto eliminata")}
                        className="flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-destructive transition-colors" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
